import { useLayoutEffect, useRef } from 'react'

/**
 * Keeps a ref pointing at the latest value, for callbacks that outlive the render they were
 * created in — a subscription, an observer, a timer — which would otherwise keep calling
 * whatever they closed over when they were set up.
 *
 * Read `.current` from those callbacks and from effects, never during render: a ref is not
 * render-safe in either direction.
 * @see https://react.dev/reference/react/useRef#caveats
 */
export default function useLatest<T>(value: T) {
  const ref = useRef(value)
  // Written in a layout effect rather than during render, so a render React throws away
  // (an interrupted transition, StrictMode's double render) cannot leave the ref pointing
  // at a value that was never committed.
  useLayoutEffect(() => {
    ref.current = value
  })

  return ref
}
